import { CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import {
  SetUpdatedObjectives,
  UpdatedObjectives,
} from 'src/app/models/Orders.model';
import { Printing } from 'src/app/models/printer';
import { OrdersService } from 'src/app/services/orders.service';
import { PrinterService } from 'src/app/services/printer/printer.service';
import { MonthlyGoalService } from 'src/app/services/shared/monthly-goal.service';
import { GoalService } from 'src/app/services/goal.service';
import { GoalDataService } from 'src/app/services/shared/goal-data-service';
import { Log } from 'src/app/services/shared/log';
import { Orders } from 'src/app/models/Orders.model';

/**
 * Settings page — printer assignments + commercial monthly objectives.
 *
 * Refactor 2026-05-25 (Chunk A): consolidated the duplicated logic of
 * `getObjectives()` and `changeYear()` into a single `loadObjectives()`
 * method; fixed the year-picker race + hardcoded default; dropped a
 * dozen of dead methods/fields; added `takeUntilDestroyed` everywhere.
 */
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  providers: [CurrencyPipe],
  standalone: false,
})


export class SettingsComponent implements OnInit, OnDestroy {

  private destroyRef = inject(DestroyRef);

  // ─── State ────────────────────────────────────────────────────────
  printers: Printing.PrinterOption[] = [];
  printerSelection: Printing.PrinterOptionSelection =
    new Printing.PrinterOptionSelection(null);

  /** Sorted 12-item array (one entry per month), plus a 13th "Annuale" row. */
  newObjectivesArray: UpdatedObjectives[] = [];
  closedOrders: Orders.IOrderClosed[] = [];

  /**
   * 🚨 FIX v2.12.6: `totalYearlyAmount` è ora un GETTER computed
   * derivato da `newObjectivesArray`. Pre-v2.12.6 era un field
   * assegnato solo dentro `loadObjectives.next` (line ~271) — quindi
   * quando `patchRowOptimistic` modificava il `net_value` di una
   * singola row dopo un save, il totale annuale e di conseguenza la
   * `yearlyProgressPercent` (la progressbar mostrata nella card
   * sommario) restavano stale finché `loadObjectives()` async
   * ricaricava dal BE — visible delay percepito come "progressbar
   * non si aggiorna" dal PM.
   *
   * Convertendolo in getter, il valore si ricomputa AD OGNI CD
   * cycle dai singoli `row.net_value`, garantendo che la progressbar
   * rifletta SEMPRE lo stato corrente dei singoli obiettivi mensili
   * mostrati nelle card sottostanti — anche durante la finestra
   * optimistic prima del refetch BE.
   *
   * `monthly_objective === 13` è la row "Annuale" appended in fondo
   * a `loadObjectives`: la escludiamo dal sum perché contiene la
   * somma stessa (duplicherebbe).
   */
  get totalYearlyAmount(): number {
    return this.newObjectivesArray
      .filter((o) => +o.monthly_objective !== 13)
      .reduce((sum, e) => sum + (+e.net_value || 0), 0);
  }

  /** Selected year for the objectives section — defaults to the current calendar year. */
  chosenYear: string = new Date().getFullYear().toString();
  years: string[] = [];

  /** Loading flag for the skeleton state on the objectives section. */
  isLoadingObjectives = true;

  /** Italian month names — used for display + matching against the API's `correspondingMonth`. */
  private readonly MONTH_NAMES_BY_INDEX: { [k: number]: string } = {
    1: 'Gennaio', 2: 'Febbraio', 3: 'Marzo', 4: 'Aprile',
    5: 'Maggio', 6: 'Giugno', 7: 'Luglio', 8: 'Agosto',
    9: 'Settembre', 10: 'Ottobre', 11: 'Novembre', 12: 'Dicembre',
  };

  /** Labels for the 4 printer toggle columns; index aligned to the legacy 0..3 code. */
  readonly PRINTER_ROLES: readonly { id: number; label: string; icon: string }[] = [
    { id: 0, label: 'DDT', icon: 'bx-file' },
    { id: 1, label: 'Scatola', icon: 'bxs-package' },
    { id: 2, label: 'Corriere', icon: 'bxs-truck' },
    { id: 3, label: 'Borderò', icon: 'bxs-receipt' },
  ];

  // Single transient field used by the currency input on blur.
  formattedAmount: any;

  constructor(
    private printerService: PrinterService,
    public orderService: OrdersService,
    public decimalPipe: DecimalPipe,
    private currencyPipe: CurrencyPipe,
    private goalService: MonthlyGoalService,
    private golServ: GoalService,
    private goalDataService: GoalDataService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeYears();
    this.loadPrinters();
    this.loadObjectives();
    this.loadClosedOrders();
  }

  ngOnDestroy(): void {
    // Persist the printer assignments. `takeUntilDestroyed` already
    // closes the live subscribes — here we only need to flush the
    // last selected state back to the service.
    this.printerService.printerSelection.next(this.printerSelection);
    void this.printerService.setPrintersSettings(this.printerSelection);
  }

  // ─── Years dropdown ─────────────────────────────────────────────
  private initializeYears(): void {
    const currentYear = new Date().getFullYear();
    this.years = Array.from({ length: 20 }, (_, i) => (currentYear - i).toString());
  }

  changeYear(year: string): void {
    // BUGFIX 2026-05-25: dropped the duplicated load path; year change
    // now simply re-runs `loadObjectives()` with the new year (which
    // also re-applies the carryover + goal-service propagation that
    // the old `changeYear()` was silently skipping).
    this.chosenYear = year;
    this.loadObjectives();
  }

  // ─── Printers ────────────────────────────────────────────────────
  private loadPrinters(): void {
    this.printerService.printers
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => { if (result) { this.printers = result; this.cdr.detectChanges(); } },
        error: (err) => Log.error('[Settings] printers subscribe failed', err),
      });
    this.printerService.printerSelection
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => { if (result) { this.printerSelection = result; this.cdr.detectChanges(); } },
        error: (err) => Log.error('[Settings] printerSelection subscribe failed', err),
      });
  }

  /**
   * Toggle which printer is assigned to a given role (DDT / Scatola
   * / Corriere / Borderò). Each role is mutually exclusive across
   * printers: assigning printer X to "DDT" unassigns whoever else
   * was holding "DDT". Used by the new template via
   * `setPrinterAssignment(printer.printer_id, role.id)`.
   */
  setPrinterAssignment(printer_id: number, role: number): void {
    this.printers = this.printers.map((p) => {
      switch (role) {
        case 0:
          this.printerSelection.printer_ddt_id = printer_id;
          p.printer_ddt = p.printer_id === printer_id;
          break;
        case 1:
          this.printerSelection.printer_box_id = printer_id;
          p.printer_box = p.printer_id === printer_id;
          break;
        case 2:
          this.printerSelection.printer_car_id = printer_id;
          p.printer_car = p.printer_id === printer_id;
          break;
        case 3:
          this.printerSelection.printer_bor_id = printer_id;
          p.printer_bor = p.printer_id === printer_id;
          break;
      }
      return p;
    });
  }

  /** Convenience: is `printer` currently assigned to the given role? */
  printerHasRole(printer: Printing.PrinterOption, role: number): boolean {
    switch (role) {
      case 0: return !!printer.printer_ddt;
      case 1: return !!printer.printer_box;
      case 2: return !!printer.printer_car;
      case 3: return !!printer.printer_bor;
      default: return false;
    }
  }

  // ─── Objectives (yearly + 12 monthly) ────────────────────────────
  /**
   * Single canonical entry point for loading the year's objectives.
   * Previously split between `getObjectives()` and `changeYear()` —
   * the latter was missing the carryover + goal propagation steps,
   * which silently broke the dashboard's "current month goal" UI
   * after every year change.
   */
  private loadObjectives(): void {
    this.isLoadingObjectives = true;
    this.orderService.getObjectives()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (!data?.success) {
            this.newObjectivesArray = [];
            this.isLoadingObjectives = false;
            this.cdr.detectChanges();
            return;
          }

          // 🚨 FIX v2.11.2 (Q6): coercion entrambi i lati. Il BE può
          // ritornare `yearly_objective` come stringa "2026" mentre
          // chosenYear è numero 2026 → strict !== escludeva il record
          // appena salvato → card mese vuota dopo Salvato. Stessa
          // coercion sul monthly_objective qualche riga sotto.
          const yearObjectives: UpdatedObjectives[] = (data.data ?? [])
            .filter((obj) => +obj.yearly_objective === +this.chosenYear);

          // Carryover: shortfalls roll forward to the next month so
          // the planned target re-balances itself (preserves the
          // legacy behaviour the manager relies on).
          const monthlyDeltas: { [k: number]: number } = {};
          let carriedOver = 0;
          for (let i = 1; i <= 12; i++) {
            const obj = yearObjectives.find(
              (o) => o.monthly_objective === i && o.yearly_objective === +this.chosenYear,
            );
            const planned = obj?.net_value || 0;
            const actual = this.golServ.getCompletedForMonth(+this.chosenYear, i - 1);
            const delta = actual - planned;
            if (delta < 0) carriedOver += -delta;
            else carriedOver = Math.max(0, carriedOver - delta);
            monthlyDeltas[i] = carriedOver;
          }

          // Build a 12-item array, one entry per month — gaps are
          // filled with empty placeholders so the UI can render a
          // consistent 4×3 grid.
          const seen = new Set<number>();
          const distinctYearObjectives = yearObjectives.filter((o) => {
            if (seen.has(o.monthly_objective)) return false;
            seen.add(o.monthly_objective);
            return true;
          });

          this.newObjectivesArray = [];
          for (let month = 1; month <= 12; month++) {
            const obj = distinctYearObjectives.find((o) => +o.monthly_objective === month);
            let row: UpdatedObjectives;
            if (obj) {
              row = new UpdatedObjectives(obj);
              row.originalNetValue = obj.net_value || 0;
              row.daily_objective = row.working_days > 0
                ? row.net_value / row.working_days
                : row.net_value;
            } 
            else {
              row = new UpdatedObjectives(null);
              row.net_value = 0;
              row.working_days = 0;
              row.daily_objective = 0;
              // 🚨 FIX v2.11.2 (Q6 final): marcatura esplicita placeholder.
              // Il commento sopra saveObjective dice che `-1 = never-saved`,
              // ma il default del class field era `0` → saveObjective
              // sbagliava la branch e mandava i nuovi obiettivi al
              // `/update_objective` endpoint con objective_id=0 → BE
              // rispondeva success: true ma `result_id: 0` (nessun record
              // aggiornato) → loadObjectives leggeva stesso array stale →
              // user vedeva reset a zero.
              row.objective_id = -1;
            }
            row.monthly_objective = month;
            row.correspondingMonth = this.MONTH_NAMES_BY_INDEX[month];
            row.yearly_objective = +this.chosenYear;
            // Normalise sentinel -1 values to 0.
            if (row.net_value === -1) row.net_value = 0;
            if (row.working_days === -1) row.working_days = 0;
            row.formattedNetValue = this.currencyPipe.transform(row.net_value, 'EUR');
            this.newObjectivesArray.push(row);
          }

          // Annual summary row appended at the end (the 13th entry).
          // `totalYearlyAmount` ora è un getter (vedi field declaration
          // sopra) → lo leggiamo direttamente, no reduce inline.
          const annual = new UpdatedObjectives(null);
          annual.monthly_objective = 13;
          annual.correspondingMonth = 'Annuale';
          annual.net_value = this.totalYearlyAmount;
          annual.yearly_objective = +this.chosenYear;
          annual.working_days = Math.max(
            1,
            this.newObjectivesArray.reduce((t, e) => t + e.working_days, 0),
          );
          annual.daily_objective = annual.net_value / annual.working_days;
          annual.formattedNetValue = this.currencyPipe.transform(annual.net_value, 'EUR');
          this.newObjectivesArray.push(annual);

          // Persist working-days back to the goal service so the
          // dashboard's daily targets stay in sync.
          this.newObjectivesArray.forEach((row) => {
            if (row.working_days > 0) {
              row.daily_objective = row.net_value / row.working_days;
              this.golServ.setWorkingDays(
                row.yearly_objective,
                row.monthly_objective - 1,
                row.working_days,
              );
            } 
            else {
              row.daily_objective = 0;
            }
          });

          // Propagate the current-month goal to the goal service so
          // the dashboard ring + day target reflect the new value.
          const currentMonthName = new Date()
            .toLocaleString('default', { month: 'long' })
            .toLowerCase();
          const currentMonthRow = this.newObjectivesArray
            .find((r) => r.correspondingMonth.toLowerCase() === currentMonthName);
          if (currentMonthRow) {
            this.goalService.setGoal(currentMonthRow.net_value);
            this.goalService.setDailyGoal(currentMonthRow.daily_objective);
          }

          this.goalDataService.setObjectives(this.newObjectivesArray);
          this.isLoadingObjectives = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Settings] getObjectives failed', err);
          this.newObjectivesArray = [];
          this.isLoadingObjectives = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * 🚨 FIX v2.11.2 (Q6 polish): optimistic in-memory patch del row del
   * mese appena salvato + force CD. Il BE risponde 200 al POST PRIMA
   * del commit DB → la GET successiva di loadObjectives leggeva dati
   * stale → newObjectivesArray ripopolato col valore vecchio → user
   * vedeva la card resettata a zero dopo "Salvato!". Reload manuale
   * della pagina poi leggeva il commit fresh e mostrava il valore
   * corretto. Patch optimistic copre l'intervallo, loadObjectives
   * resta come safety net che recupera totalYearlyAmount + carryover.
   */
  private patchRowOptimistic(objective: UpdatedObjectives): void {
    const row = this.newObjectivesArray.find(
      (o) => +o.monthly_objective === +objective.monthly_objective,
    );
    if (!row) return;
    row.net_value = +objective.net_value || 0;
    row.working_days = +objective.working_days || 0;
    row.daily_objective =
      row.working_days > 0 ? row.net_value / row.working_days : row.net_value;
    row.originalNetValue = row.net_value;
    row.formattedNetValue = this.currencyPipe.transform(row.net_value, 'EUR');
    // NB: NON tocchiamo objective_id qui. Resta a -1 (placeholder)
    // finché loadObjectives non ricarica e legge il vero id assegnato
    // dal BE. Cambiarlo a 0/1 confonderebbe il prossimo saveObjective.
    this.cdr.detectChanges();
  }

  // ─── Persisting one month's objective (create / update) ──────────
  setGoal(objective: UpdatedObjectives): void {
    const payload = new SetUpdatedObjectives({
      working_days: objective.working_days,
      monthly_objective: objective.monthly_objective,
      yearly_objective: +this.chosenYear,
      net_value: objective.net_value,
    });
    this.orderService.setObjectives(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.patchRowOptimistic(objective);
          this.showSuccess();
          this.loadObjectives();
        },
        error: (err) => {
          Log.error('[Settings] setObjectives failed', err);
          this.showError('Errore', 'Non è stato possibile salvare l\'obiettivo.');
        },
      });
  }

  updateGoal(obj: UpdatedObjectives): void {
    if (obj.working_days < 0) obj.working_days = 0;
    this.orderService.updateObjective(obj)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (data) {
            this.patchRowOptimistic(obj);
            this.showSuccess();
            this.loadObjectives();
          }
        },
        error: (err) => {
          Log.error('[Settings] updateObjective failed', err);
          this.showError('Errore', 'Non è stato possibile aggiornare l\'obiettivo.');
        },
      });
  }

  /**
   * Il row.objective_id è `-1` per un placeholder (never-saved) e un id
   * reale (>0) altrimenti. Il template usa questo flag per decidere
   * POST a `/set_objective` (create) vs `/update_objective` (update by id).
   *
   * 🚨 FIX v2.11.2 (Q6 final): cambiato check da `=== -1` a `<= 0` per
   * catturare anche il caso `objective_id = 0`. Il legacy partiva dal
   * presupposto che placeholder fosse marcato `-1` ma il default del
   * field class era `0`, e loadObjectives non lo settava esplicitamente
   * → tutti i placeholder finivano su updateGoal con objective_id=0 →
   * BE rifiutava silenziosamente con `result_id: 0` → user vedeva reset.
   */
  saveObjective(obj: UpdatedObjectives): void {
    if (obj.working_days === 0 || obj.net_value === 0) return;
    if (!obj.objective_id || obj.objective_id <= 0) this.setGoal(obj);
    else this.updateGoal(obj);
  }

  // ─── Input wiring ────────────────────────────────────────────────
  onInputChange(event: Event, row: UpdatedObjectives): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const cleaned = parseFloat(inputValue.replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(cleaned)) {
      row.net_value = cleaned;
      this.formattedAmount = inputValue;
      if (this.isCurrentMonth(row)) {
        this.goalService.setGoal(cleaned);
      }
    }
  }

  transformAmount(event: Event): void {
    this.formattedAmount = this.currencyPipe.transform(this.formattedAmount, 'EUR');
    (event.target as HTMLInputElement).value = this.formattedAmount ?? '';
  }

  isCurrentMonth(row: UpdatedObjectives): boolean {
    const currentMonth = new Date()
      .toLocaleString('default', { month: 'long' })
      .toLowerCase();
    return row?.correspondingMonth?.toLowerCase() === currentMonth;
  }

  // ─── Display helpers ─────────────────────────────────────────────
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  /**
   * 🚨 FIX v2.12.6: progressbar mostrava sempre 0% indipendentemente
   * dagli obiettivi impostati nelle card sottostanti (segnalato dal
   * PM). Root cause: la formula legacy era `completed / totalYearlyAmount`
   * dove `completed` veniva da `golServ.getCompletedForMonth()` che
   * legge `goalHistory[key]?.completed`. Ma il SETTER `setMonthlyProgress`
   * non è MAI chiamato da nessuna parte del codebase — `goalHistory`
   * resta sempre vuoto → `getCompletedForMonth` ritorna sempre 0 →
   * progressbar sempre 0% per design legacy mai completato.
   *
   * Nuova semantica (allineata alla richiesta PM): la progressbar mostra
   * **quanti dei 12 mesi sono stati configurati** (status `saved` o
   * `current`). È esattamente "valore aggiornato a seconda degli
   * obiettivi impostati nelle card sottostanti" — più si compilano
   * card, più la barra si riempie. Si auto-aggiorna ad ogni CD cycle
   * via il getter.
   */
  get completedYearlyAmount(): number {
  return this.closedOrders
    .filter((o) => {
      if (!o.or_closed_date) return false;

      const closedYear = new Date(o.or_closed_date).getFullYear();

      return closedYear === +this.chosenYear;
    })
    .reduce((sum, o) => {
      return sum + (+o.or_net_value || 0);
    }, 0);
}

get yearlyProgressPercent(): number {
  if (this.totalYearlyAmount <= 0) return 0;

  return Math.min(
    100,
    Math.round(
      (this.completedYearlyAmount / this.totalYearlyAmount) * 100
    ),
  );
}

  /** Numero di mesi configurati (saved + current) per il label sotto
   *  la progressbar — più chiaro di "X% completato". */
  get configuredMonthsCount(): number {
    return this.newObjectivesArray
      .filter((o) => +o.monthly_objective >= 1 && +o.monthly_objective <= 12)
      .filter((o) => this.getMonthStatus(o) !== 'empty')
      .length;
  }

  /** Status badge used by each month card. */
  getMonthStatus(row: UpdatedObjectives): 'current' | 'saved' | 'empty' {
    if (this.isCurrentMonth(row)) return 'current';
    if (row.net_value > 0 && row.working_days > 0 && row.objective_id !== -1) return 'saved';
    return 'empty';
  }

  // ─── User feedback toasts ────────────────────────────────────────
  // Cleanup 2026-05-25: tone-of-voice cleaned up. The legacy
  // implementation insulted the user ("Non sei stato a scuola?!");
  // unacceptable in a management portal.
  private showSuccess(): void {
    Swal.fire({
      icon: 'success',
      title: 'Salvato',
      text: 'Obiettivo aggiornato con successo.',
      timer: 1500,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  }

  private showError(title: string, text: string): void {
    Swal.fire({
      icon: 'error',
      title,
      text,
      timer: 2200,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  }

  private loadClosedOrders(): void {
  this.orderService.getClosedOrders()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (orders) => {
        this.closedOrders = orders ?? [];
        this.cdr.detectChanges();
      },
      error: (err) => {
        Log.error('[Settings] getClosedOrders failed', err);
      },
    });
}


  
}
